import { useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  parseViewStateFromUrl,
  hasViewStateInUrl,
  updateUrlWithViewState,
  ViewStateParams
} from '../../utils/viewStateDeepLink';
import {
  shouldApplyViewStateFromUrlOnMount,
  markSharedViewUrlStateApplied
} from '../../utils/sharedViewMode';

export interface UseViewStateDeepLinkOptions {
  /**
   * If true, automatically applies URL state on mount
   * Default: true
   */
  autoApplyOnMount?: boolean;

  /**
   * If true, automatically syncs state changes to URL
   * Default: true
   */
  autoSyncToUrl?: boolean;

  /**
   * Callback fired when URL state should be applied
   * This is where you dispatch actions to update your Redux store
   */
  onApplyUrlState?: (params: ViewStateParams) => void;

  /**
   * Function to get current view state for syncing to URL
   */
  getCurrentViewState?: () => ViewStateParams;
}

export interface UseViewStateDeepLinkReturn {
  /**
   * Manually parse and return current URL state
   */
  getUrlState: () => ViewStateParams;

  /**
   * Check if URL has any view state parameters
   */
  hasUrlState: () => boolean;

  /**
   * Manually apply URL state (calls onApplyUrlState)
   */
  applyUrlState: () => void;

  /**
   * Update URL with provided view state
   */
  syncToUrl: (params: ViewStateParams) => void;

  /**
   * Copy current view as deep link to clipboard
   */
  copyDeepLink: () => Promise<void>;
}

/**
 * Hook to manage view state deep linking
 * Automatically applies URL state on mount and syncs changes to URL
 */
export const useViewStateDeepLink = (
  options: UseViewStateDeepLinkOptions = {}
): UseViewStateDeepLinkReturn => {
  const {
    autoApplyOnMount = true,
    autoSyncToUrl = true,
    onApplyUrlState,
    getCurrentViewState
  } = options;

  const location = useLocation();
  const navigate = useNavigate();
  const hasAppliedInitialState = useRef(false);

  /**
   * Get current URL state
   */
  const getUrlState = useCallback((): ViewStateParams => {
    return parseViewStateFromUrl();
  }, []);

  /**
   * Check if URL has view state
   */
  const hasUrlState = useCallback((): boolean => {
    return hasViewStateInUrl();
  }, []);

  /**
   * Apply URL state by calling the callback
   */
  const applyUrlState = useCallback(() => {
    const urlParams = getUrlState();
    if (Object.keys(urlParams).length > 0 && onApplyUrlState) {
      onApplyUrlState(urlParams);
    }
  }, [getUrlState, onApplyUrlState]);

  /**
   * Sync current view state to URL
   */
  const syncToUrl = useCallback(
    (params: ViewStateParams) => {
      if (autoSyncToUrl) {
        updateUrlWithViewState(params, true);
      }
    },
    [autoSyncToUrl]
  );

  /**
   * Copy current view as deep link
   */
  const copyDeepLink = useCallback(async () => {
    if (getCurrentViewState) {
      const currentState = getCurrentViewState();
      const link = new URL(
        window.location.origin + window.location.pathname
      ).toString();

      // Generate URL with current state
      updateUrlWithViewState(currentState, true);

      // Copy to clipboard
      await navigator.clipboard.writeText(window.location.href);

      return;
    }

    // Fallback: just copy current URL
    await navigator.clipboard.writeText(window.location.href);
  }, [getCurrentViewState]);

  /**
   * Apply URL state on mount (only once)
   * This ensures deep links work and take precedence over local storage
   */
  useEffect(() => {
    if (autoApplyOnMount && !hasAppliedInitialState.current) {
      if (shouldApplyViewStateFromUrlOnMount()) {
        applyUrlState();
        markSharedViewUrlStateApplied();
      }

      hasAppliedInitialState.current = true;
    }
  }, [autoApplyOnMount, applyUrlState]);

  return {
    getUrlState,
    hasUrlState,
    applyUrlState,
    syncToUrl,
    copyDeepLink
  };
};
